import { useState, useEffect } from 'react';
import { getSubscriptionStatus } from '../services/auth';

/**
 * SubscriptionStatus - Displays subscription and usage information.
 */
export function SubscriptionStatus() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  async function fetchStatus() {
    setLoading(true);
    setError(null);
    
    try {
      const status = await getSubscriptionStatus();
      setData(status);
    } catch (err) {
      console.error('Failed to fetch subscription status:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="subscription-status loading">
        <span className="spinner"></span>
        <span>Loading...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subscription-status error">
        <span>Error: {error}</span>
        <button onClick={fetchStatus} className="btn-link">
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  const { subscription, usage } = data;
  const statusColors = {
    active: 'status-active',
    pending: 'status-pending',
    unsubscribed: 'status-inactive',
    failed: 'status-error',
  };

  return (
    <div className="subscription-status">
      <div className="subscription-header">
        <h3>Subscription Status</h3>
        <span className={`status-badge ${statusColors[subscription?.status] || ''}`}>
          {subscription?.status === 'active' ? 'Active' :
           subscription?.status === 'pending' ? 'Pending' :
           subscription?.status === 'unsubscribed' ? 'Cancelled' :
           subscription?.status || 'Unknown'}
        </span>
      </div>

      {subscription && (
        <div className="subscription-details">
          {subscription.company_name && (
            <div className="detail-row">
              <span className="detail-label">Company:</span>
              <span className="detail-value">{subscription.company_name}</span>
            </div>
          )}
          {subscription.email && (
            <div className="detail-row">
              <span className="detail-label">Email:</span>
              <span className="detail-value">{subscription.email}</span>
            </div>
          )}
        </div>
      )}

      {usage && (
        <div className="usage-section">
          <h4>Period Usage</h4>
          <div className="usage-stats">
            <div className="usage-stat">
              <span className="stat-value">{usage.total_documents || 0}</span>
              <span className="stat-label">Documents Classified</span>
            </div>
            <div className="usage-stat success">
              <span className="stat-value">{usage.successful_metering || 0}</span>
              <span className="stat-label">Successful</span>
            </div>
            {usage.failed_metering > 0 && (
              <div className="usage-stat error">
                <span className="stat-value">{usage.failed_metering}</span>
                <span className="stat-label">Failed</span>
              </div>
            )}
          </div>
          {usage.period_start && usage.period_end && (
            <p className="usage-period">
              Period: {new Date(usage.period_start).toLocaleDateString()} - {new Date(usage.period_end).toLocaleDateString()}
            </p>
          )}
        </div>
      )}

      <div className="subscription-actions">
        <a 
          href="https://aws.amazon.com/marketplace/library" 
          target="_blank" 
          rel="noopener noreferrer"
          className="btn-secondary"
        >
          Manage Subscription
        </a>
        <button onClick={fetchStatus} className="btn-link">
          Refresh
        </button>
      </div>
    </div>
  );
}
